import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import ForecastLayout from "../../components/forecast/ForecastLayout";
import { KpiStrip, DriverPanel, LineButton, PageState, Cue, originOf } from "../../components/forecast/Lines";
import { OriginBadge } from "../../lib/origin.jsx";
import { usePage, whenText } from "../../lib/forecastApi";

function ModelCard({ card, i }) {
  return (
    <div className="tile flex flex-col kc-rise" style={{ animationDelay: `${i * 60}ms` }} data-testid={`fc-card-${card.id}`}>
      <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted font-semibold">Model {String(i + 1).padStart(2, "0")}</div>
        <OriginBadge {...originOf(card.headline, card.title)} testid={`fc-card-${card.id}-origin`} />
      </div>
      <Link to={card.route} data-testid={`fc-card-${card.id}-open`} className="px-4 font-display font-semibold text-[15px] leading-snug hover:text-primary transition-colors inline-flex items-start gap-1">
        {card.title}<ArrowUpRight size={14} className="mt-0.5 shrink-0" />
      </Link>
      <div className="mt-auto pt-3">
        {[card.headline, card.second].map((l, j) => (
          <LineButton key={l.id} line={l} className="px-4 py-2.5 border-t border-border">
            <div className="text-[11px] text-muted truncate">{l.label}</div>
            <div className="flex items-baseline justify-between gap-2">
              <span className={j === 0 ? "kpi-value text-xl" : "font-mono-tabular text-sm font-semibold"}>{l.display}</span><Cue />
            </div>
          </LineButton>
        ))}
      </div>
    </div>
  );
}

export default function ForecastOverview() {
  const { data, error, forbidden, reload } = usePage("overview");
  return (
    <ForecastLayout title="Forecast / Prediction Models"
      description="What demand will do, and what that means for stockouts, reorders, aging stock and heat — built from the seven engines, the ABC × XYZ classification and NASA POWER weather. Click any line for an AI explanation and its source rows.">
      {!data ? <PageState error={error} forbidden={forbidden} onRetry={reload} /> : (
        <>
          <KpiStrip kpis={data.kpis} />
          <div className={`grid grid-cols-1 gap-6 ${data.forecast_drivers.length && data.prediction_drivers.length ? "xl:grid-cols-2" : ""}`}>
            {!!data.forecast_drivers.length && <DriverPanel testid="fc-forecast-drivers" title="Forecast drivers" hint="What moves demand · estimated effect on the forecast" drivers={data.forecast_drivers} centered />}
            {!!data.prediction_drivers.length && <DriverPanel testid="fc-prediction-drivers" title="Prediction drivers" hint="What drives the risk models · share of results it affects" drivers={data.prediction_drivers} />}
          </div>
          <div>
            <div className="kpi-label mb-3">Models</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {data.cards.map((c, i) => <ModelCard key={c.id} card={c} i={i} />)}
            </div>
          </div>
          <div className="text-[11px] text-muted font-mono-tabular" data-testid="fc-meta">
            As of {data.meta.as_of} · engine cycle {data.meta.engine_run_id || "—"} ({whenText(data.meta.engine_run_at)}) · sample data until actual 1Rotary data is switched on
          </div>
        </>
      )}
    </ForecastLayout>
  );
}
